import { useEffect, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Conversation } from '../lib/types';

/**
 * The AI-Flagged worklist (F11) as a self-contained data source, so the tab
 * badge and the tab list are ALWAYS the same set — the old design counted
 * flagged chats with one query (active statuses) and listed them from another
 * (newest ~100 of any status), so the number and the list disagreed.
 *
 * Queried directly on `aiFlagged.at` (present only while a chat is flagged, any
 * status, cleared by "mark reviewed"), newest first. Inbox scope is applied
 * client-side to avoid a composite index.
 */
export function useFlaggedChats(inboxIds?: string[]): Conversation[] {
  const [chats, setChats] = useState<Conversation[]>([]);
  const inboxKey = (inboxIds ?? []).slice().sort().join(',');

  useEffect(() => {
    const q = query(
      collection(db, 'chats'),
      where('aiFlagged.at', '>', 0),
      orderBy('aiFlagged.at', 'desc'),
      fsLimit(100),
    );
    const allowed = inboxIds && inboxIds.length > 0 ? new Set(inboxIds) : null;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: Conversation[] = [];
        for (const d of snap.docs) {
          const data = { id: d.id, ...(d.data() as object) } as Conversation & { inboxId?: string };
          if (allowed && (!data.inboxId || !allowed.has(data.inboxId))) continue;
          rows.push(data);
        }
        setChats(rows);
      },
      (err) => console.error('useFlaggedChats listener error', err),
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxKey]);

  return chats;
}
